import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { Box, useTheme, Chip, IconButton, useMediaQuery, Button, Typography, Checkbox, Autocomplete } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { HFilledTextField } from '@ui/components/common/TextField';
import { getEventLanguagesByEventIdAsync } from '@application/features/languages/languagesThunk';
import { getAllCompaniesAsync } from '@application/features/company/companyThunk';
import DataTable from '@ui/components/common/DataTable';
import { StyledMenu } from '@ui/components/common/Menu';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import SelectRSVPsDialog from './SelectMVPsDialog';
import ImportExcelDialog from './ImportExcelDialog';
import AddExternalRelationDialog from './AddExternalRelationDialog';
import { Iconly } from 'react-iconly';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAllMVPsAsync, deleteMVPAsync, sendInvitationAsync, sendInvitationsBulkAsync, exportInvitedMVPsAsync, sendReinvitationEmailAsync } from '@application/features/mvps/mvpsThunk';
import { MVPStatusEnum } from '@application/enums/mvp-status-enum';
import { Trash, Sms, DirectInbox, DocumentDownload } from 'iconsax-react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { KeyboardArrowDown } from '@mui/icons-material';
import { hasPermission } from '@application/helpers/permission-helper';
const RSVPTrackingTab = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(1200));
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    const mvpsState = useAppSelector((state) => state.mvps);
    const { VDKEventsForEventDetails } = useAppSelector((state) => state.VDKEvent);
    const isExternalAllowed = VDKEventsForEventDetails?.isExternalAllowed ?? false;
    const loggedInUserPermissions = useAppSelector((state) => state.auth.currentUser?.permissions || []);
    const canUpdateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    // Local pagination state (DataTable uses 0-based page index)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [openInternalDialog, setOpenInternalDialog] = useState(false);
    const [openExternalDialog, setOpenExternalDialog] = useState(false);
    const [openAddExternalRelationDialog, setOpenAddExternalRelationDialog] = useState(false);
    const [openImportExcelDialog, setOpenImportExcelDialog] = useState(false);
    const [shouldOpenMenu, setShouldOpenMenu] = useState(false);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [mvpToDelete, setMvpToDelete] = useState(null);
    const [sendEmailConfirmOpen, setSendEmailConfirmOpen] = useState(false);
    const [mvpToSendEmail, setMvpToSendEmail] = useState(null);
    const [bulkSendEmailConfirmOpen, setBulkSendEmailConfirmOpen] = useState(false);
    const [mvpToEdit, setMvpToEdit] = useState(null);
    const [editDialogType, setEditDialogType] = useState('internal');
    const [selectedRows, setSelectedRows] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [filters, setFilters] = useState({});
    const [tempFilters, setTempFilters] = useState({});
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [sortBy, setSortBy] = useState(undefined);
    const [sortOrder, setSortOrder] = useState(undefined);
    const { eventLanguages } = useAppSelector((state) => state.languages);
    const { allCompanies } = useAppSelector((state) => state.company);
    useEffect(() => {
        if (eventId) {
            dispatch(getEventLanguagesByEventIdAsync(eventId));
        }
        dispatch(getAllCompaniesAsync());
    }, [eventId, dispatch]);
    const languageFilterOptions = useMemo(() => (eventLanguages || []).map((lang) => ({
        label: lang.name,
        value: lang.id,
        flagUrl: lang.flagUrl
    })), [eventLanguages]);
    const companyFilterOptions = useMemo(() => (allCompanies || []).map((c) => ({ label: c.name, value: c.name })), [allCompanies]);
    const statusFilterOptions = useMemo(() => [
        { label: t('events.eventDetails.invitationsTab.pending') || 'Pending', value: MVPStatusEnum.Pending },
        { label: t('events.eventDetails.invitationsTab.invited') || 'Invited', value: MVPStatusEnum.Invited },
        { label: t('events.eventDetails.invitationsTab.registered') || 'Registered', value: MVPStatusEnum.Registered }
    ], [t]);
    const isExternalFilterOptions = useMemo(() => [
        { label: t('common.yes') || 'Yes', value: true },
        { label: t('common.no') || 'No', value: false }
    ], [t]);
    // Fetch MVPs whenever eventId / pagination / search / filters change
    useEffect(() => {
        if (!eventId)
            return;
        const payload = {
            eventId,
            pageNumber: page + 1,
            pageSize: rowsPerPage,
            searchString: searchValue || undefined,
            firstName: filters.firstName || undefined,
            lastName: filters.lastName || undefined,
            company: filters.company || undefined,
            status: filters.status ?? undefined,
            isExternal: filters.isExternal ?? undefined,
            languageId: filters.language || undefined,
            sortBy,
            sortOrder
        };
        dispatch(getAllMVPsAsync(payload));
        // Clear selections when page changes
        setSelectedRows([]);
    }, [dispatch, eventId, page, rowsPerPage, searchValue, filters, sortBy, sortOrder]);
    const rows = useMemo(() => {
        if (!mvpsState.mvps?.items)
            return [];
        return mvpsState.mvps.items.map((mvp) => ({
            id: mvp.id,
            firstName: mvp.firstName,
            lastName: mvp.lastName,
            email: mvp.email,
            referredBy: mvp.referredBy,
            // Keep the enum value from backend
            status: mvp.status,
            company: mvp.company,
            isExternal: !mvp.isInternal,
            registrationId: mvp.registrationId,
            preferredLanguage: mvp.preferredLanguage
        }));
    }, [mvpsState.mvps]);
    useEffect(() => {
        if (shouldOpenMenu) {
            // Find the add button in the DOM by searching all buttons
            const buttons = Array.from(document.querySelectorAll('button'));
            const button = buttons.find((btn) => btn.textContent?.includes(t('events.eventDetails.rsvpTracking.addRSVPs') || 'Add RSVPs') ||
                btn
                    .getAttribute('aria-label')
                    ?.includes(t('events.eventDetails.rsvpTracking.addRSVPs') || 'Add RSVPs'));
            if (button) {
                setMenuAnchorEl(button);
                setShouldOpenMenu(false);
            }
            else {
                // If button not found, try again after a short delay
                const timeout = setTimeout(() => {
                    const retryButtons = Array.from(document.querySelectorAll('button'));
                    const retryButton = retryButtons.find((btn) => btn.textContent?.includes(t('events.eventDetails.rsvpTracking.addRSVPs') || 'Add RSVPs') || btn.textContent?.includes('+ Add RSVPs'));
                    if (retryButton) {
                        setMenuAnchorEl(retryButton);
                    }
                    setShouldOpenMenu(false);
                }, 100);
                return () => clearTimeout(timeout);
            }
        }
    }, [shouldOpenMenu]);
    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
    };
    const handleInternalClick = (event) => {
        setOpenInternalDialog(true);
        handleCloseMenu();
    };
    const handleExternalClick = (event) => {
        setOpenExternalDialog(true);
        handleCloseMenu();
    };
    const handleImportExcelClick = (event) => {
        setOpenImportExcelDialog(true);
        handleCloseMenu();
    };
    const handleEdit = (row) => {
        // Find the original MVP DTO from the state
        const originalMvp = mvpsState.mvps?.items?.find((m) => m.id === row.id);
        if (originalMvp) {
            setMvpToEdit(originalMvp);
            setEditDialogType(originalMvp.isInternal ? 'internal' : 'external');
            if (originalMvp.isInternal) {
                setOpenInternalDialog(true);
            }
            else {
                setOpenExternalDialog(true);
            }
        }
    };
    const handleLink = (row) => { };
    const handleDelete = (row) => {
        // Find the original MVP DTO from the state
        const originalMvp = mvpsState.mvps?.items?.find((m) => m.id === row.id);
        if (originalMvp) {
            setMvpToDelete(originalMvp);
            setDeleteConfirmationOpen(true);
        }
    };
    const handleSendEmail = async (row) => {
        try {
            await dispatch(sendInvitationAsync(row.id))
                .unwrap()
                .then((response) => {
                if (response && response.isSuccess) {
                    toast.success(t('events.eventDetails.rsvpTracking.invitationSentSuccessfully') ||
                        'Invitation sent successfully!');
                }
                else {
                    toast.error(response.message ||
                        t('events.eventDetails.rsvpTracking.failedToSendInvitation') ||
                        'Failed to send invitation');
                }
            });
            // Refresh the MVP list
            if (eventId) {
                const payload = {
                    eventId,
                    pageNumber: page + 1,
                    pageSize: rowsPerPage
                };
                dispatch(getAllMVPsAsync(payload));
            }
        }
        catch (error) {
            const errorMessage = error?.error ||
                error?.message ||
                t('events.eventDetails.rsvpTracking.failedToSendInvitation') ||
                'Failed to send invitation';
            toast.error(errorMessage);
        }
    };
    const handleSendReinvitationEmail = async (row) => {
        try {
            await dispatch(sendReinvitationEmailAsync(row.id)).unwrap().then((response) => {
                if (response && response.isSuccess) {
                    toast.success(t('events.eventDetails.rsvpTracking.invitationSentSuccessfully') ||
                        'Invitation sent successfully!');
                }
                else {
                    toast.error(response.message || t('events.eventDetails.rsvpTracking.failedToSendInvitation') || 'Failed to send invitation');
                }
            });
        }
        catch (error) {
            toast.error(error?.error ||
                error?.message ||
                t('events.eventDetails.rsvpTracking.failedToSendReinvitation') ||
                'Failed to send re-invitation email');
        }
    };
    const handleExportInvited = async () => {
        if (!eventId)
            return;
        try {
            const data = await dispatch(exportInvitedMVPsAsync(eventId)).unwrap();
            if (!data.length) {
                toast.warning(t('events.eventDetails.rsvpTracking.noInvitedToExport') || 'No invited invitees to export');
                return;
            }
            const rows = data.map((d) => ({
                [t('labels.firstName') || 'First Name']: d.firstName,
                [t('labels.lastName') || 'Last Name']: d.lastName,
                [t('labels.email') || 'Email']: d.email,
                [t('labels.company') || 'Company']: d.company,
                [t('events.eventDetails.rsvpTracking.referredBy') || 'Referred By']: d.referredBy,
                [t('events.eventDetails.rsvpTracking.type') || 'Type']: d.type,
                [t('events.eventDetails.rsvpTracking.invitedDate') || 'Invited Date']: d.invitedDate
                    ? new Date(d.invitedDate).toLocaleDateString()
                    : ''
            }));
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Invited');
            const now = new Date();
            const dd = String(now.getDate()).padStart(2, '0');
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const yyyy = now.getFullYear();
            XLSX.writeFile(wb, `Invites${dd}${mm}${yyyy}.xlsx`);
            toast.success(t('events.eventDetails.rsvpTracking.exportSuccess') || 'Exported successfully');
        }
        catch {
            toast.error(t('events.eventDetails.rsvpTracking.exportFailed') || 'Failed to export');
        }
    };
    const handleSendBulkEmails = async () => {
        if (selectedRows.length === 0)
            return;
        try {
            const results = await dispatch(sendInvitationsBulkAsync({ mvpIds: selectedRows })).unwrap();
            const successCount = results.filter((r) => r.success).length;
            const failCount = results.length - successCount;
            const failedItems = results.filter((r) => !r.success);
            if (successCount > 0) {
                toast.success(t('events.eventDetails.rsvpTracking.bulkInvitationsSentSuccessfully', {
                    count: successCount
                }) || `Successfully sent ${successCount} invitation${successCount > 1 ? 's' : ''}`);
            }
            if (failCount > 0) {
                // Show detailed error messages if available
                const errorMessages = failedItems
                    .map((r) => r.error)
                    .filter((msg) => Boolean(msg))
                    .join('; ');
                const errorMessage = errorMessages
                    ? `${t('events.eventDetails.rsvpTracking.bulkInvitationsFailed', { count: failCount }) || `Failed to send ${failCount} invitation${failCount > 1 ? 's' : ''}`}: ${errorMessages}`
                    : t('events.eventDetails.rsvpTracking.bulkInvitationsFailed', { count: failCount }) ||
                        `Failed to send ${failCount} invitation${failCount > 1 ? 's' : ''}`;
                toast.error(errorMessage);
            }
            setSelectedRows([]);
            // Refresh the MVP list
            if (eventId) {
                const payload = {
                    eventId,
                    pageNumber: page + 1,
                    pageSize: rowsPerPage
                };
                dispatch(getAllMVPsAsync(payload));
            }
        }
        catch (error) {
            // Handle API-level errors (network, 500, etc.)
            let errorMessage = t('events.eventDetails.rsvpTracking.failedToSendBulkInvitations') ||
                'Failed to send invitations';
            if (error?.error && typeof error.error === 'string') {
                errorMessage = error.error;
            }
            else if (error?.message && typeof error.message === 'string') {
                errorMessage = error.message;
            }
            else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            else if (error?.response?.data?.error) {
                errorMessage = error.response.data.error;
            }
            toast.error(errorMessage);
            setSelectedRows([]);
        }
    };
    const handleConfirmDelete = async () => {
        if (mvpToDelete) {
            try {
                await dispatch(deleteMVPAsync(mvpToDelete.id)).unwrap();
                // Show success message
                toast.success(t('events.eventDetails.rsvpTracking.rsvpDeletedSuccessfully') ||
                    'RSVP deleted successfully!');
                // Refresh the MVP list
                if (eventId) {
                    const payload = {
                        eventId,
                        pageNumber: page + 1,
                        pageSize: rowsPerPage
                    };
                    dispatch(getAllMVPsAsync(payload));
                }
                setDeleteConfirmationOpen(false);
                setMvpToDelete(null);
            }
            catch (error) {
                // Extract error message from backend API response
                let errorMessage = t('events.eventDetails.rsvpTracking.failedToDeleteRSVP') ||
                    'Failed to delete RSVP. Please try again.';
                if (error) {
                    if (error.error && typeof error.error === 'string') {
                        errorMessage = error.error;
                    }
                    else if (error.message && typeof error.message === 'string') {
                        errorMessage = error.message;
                    }
                }
                toast.error(errorMessage);
            }
        }
    };
    const columns = [
        {
            key: 'checkbox',
            label: (_jsx(Checkbox, { checked: rows.length > 0 &&
                    selectedRows.length ===
                        rows.filter((r) => r.status !== MVPStatusEnum.Registered && r.status !== MVPStatusEnum.Invited).length &&
                    rows.filter((r) => r.status !== MVPStatusEnum.Registered && r.status !== MVPStatusEnum.Invited).length > 0, indeterminate: selectedRows.length > 0 &&
                    selectedRows.length <
                        rows.filter((r) => r.status !== MVPStatusEnum.Registered && r.status !== MVPStatusEnum.Invited).length, onChange: (e) => {
                    if (e.target.checked) {
                        setSelectedRows(rows
                            .filter((r) => r.status !== MVPStatusEnum.Registered && r.status !== MVPStatusEnum.Invited)
                            .map((r) => r.id));
                    }
                    else {
                        setSelectedRows([]);
                    }
                }, onClick: (e) => e.stopPropagation() })),
            align: 'left',
            width: 50,
            render: (row) => (_jsx(Checkbox, { checked: selectedRows.includes(row.id), disabled: row.status === MVPStatusEnum.Registered || row.status === MVPStatusEnum.Invited, onChange: (e) => {
                    if (e.target.checked) {
                        setSelectedRows([...selectedRows, row.id]);
                    }
                    else {
                        setSelectedRows(selectedRows.filter((id) => id !== row.id));
                    }
                }, onClick: (e) => e.stopPropagation() }))
        },
        {
            key: 'firstName',
            label: t('labels.firstName') || 'First Name',
            align: 'left',
            sortable: true
        },
        {
            key: 'lastName',
            label: t('labels.lastName') || 'Last Name',
            align: 'left',
            sortable: true
        },
        {
            key: 'email',
            label: t('labels.email') || 'Email',
            align: 'left',
            sortable: true
        },
        {
            key: 'referredBy',
            label: t('events.eventDetails.rsvpTracking.referredBy') || 'Referred by',
            align: 'left',
            sortable: true
        },
        {
            key: 'status',
            label: t('events.eventDetails.invitationsTab.status') || 'Status',
            align: 'left',
            sortable: true,
            render: (row) => {
                let statusLabel = 'Pending';
                let chipColor = 'warning';
                if (row.status === MVPStatusEnum.Registered) {
                    statusLabel = 'Registered';
                    chipColor = 'success';
                }
                else if (row.status === MVPStatusEnum.Invited) {
                    statusLabel = 'Invited';
                    chipColor = 'info';
                }
                return _jsx(Chip, { label: statusLabel, variant: 'soft', size: 'medium', color: chipColor });
            }
        },
        {
            key: 'company',
            label: t('labels.company') || 'Company',
            align: 'left',
            sortable: true
        },
        {
            key: 'isExternal',
            label: t('events.eventDetails.rsvpTracking.isExternal') || 'Is External',
            align: 'left',
            sortable: true,
            render: (row) => (_jsx(Typography, { variant: 'body2', children: row.isExternal ? t('common.yes') || 'Yes' : t('common.no') || 'No' }))
        },
        {
            key: 'preferredLanguage',
            label: t('events.eventDetails.rsvpTracking.invitationLanguage') || 'Invitation Language',
            align: 'left',
            sortable: true,
            sortKey: 'preferredLanguage',
            render: (row) => row.preferredLanguage ? (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [row.preferredLanguage.flagUrl && (_jsx(Box, { component: 'img', src: row.preferredLanguage.flagUrl, alt: row.preferredLanguage.name, sx: { width: 20, height: 15, objectFit: 'cover', borderRadius: 0.5 } })), _jsx(Typography, { variant: 'body2', children: row.preferredLanguage.name })] })) : (_jsx(Typography, { variant: 'body2', children: "-" }))
        }
    ];
    return (_jsxs(Box
    // sx={{
    //   backgroundColor: theme.palette.common.white92,
    //   p: theme.spacing(2.5),
    //   borderRadius: 3,
    //   overflow: 'hidden'
    // }}
    , { children: [_jsx(DataTable, { data: rows, columns: columns, getRowId: (row) => row.id, title: t('events.eventDetails.rsvpTracking.title') || 'RSVP Tracking', showSearchFilter: false, sortBy: sortBy, sortOrder: sortOrder, onSortChange: (field, order) => {
                    setSortBy(field);
                    setSortOrder(order);
                    setPage(0);
                }, showAddButton: canUpdateEvents, addButtonLabel: t('events.eventDetails.rsvpTracking.addRSVPs') || 'Add RSVPs', onAddClick: () => {
                    if (!canUpdateEvents) {
                        toast.error(t('common.unauthorized') || 'You do not have permission to add RSVPs');
                        return;
                    }
                    setShouldOpenMenu(true);
                }, customToolbarLeft: _jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' }, children: [_jsx(Button, { variant: 'outlined', color: Object.keys(filters).length > 0 ? 'primary' : 'inherit', size: 'large', startIcon: _jsx(Iconly, { name: 'Filter', set: 'curved', style: { width: 20, height: 20 } }), onClick: () => {
                                setTempFilters(filters);
                                setFilterDialogOpen(true);
                            }, children: t('actions.filters') || 'Filters' }), Object.keys(filters).length > 0 && (_jsx(IconButton, { size: 'small', onClick: () => {
                                setFilters({});
                                setTempFilters({});
                                setPage(0);
                            }, sx: { border: 'none' }, children: _jsx(Icon, { icon: 'eva:close-fill' }) })), selectedRows.length > 0 && (_jsxs(Button, { variant: 'outlined', color: 'primary', size: 'large', startIcon: _jsx(DirectInbox, { variant: 'Outline', size: 20 }), disabled: !canUpdateEvents, onClick: () => setBulkSendEmailConfirmOpen(true), children: [t('actions.sendEmail') || 'Send Email', " (", selectedRows.length, ")"] })), _jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', startIcon: _jsx(DocumentDownload, { variant: 'Outline', size: 20 }), onClick: handleExportInvited, children: t('events.eventDetails.rsvpTracking.exportInvited') || 'Export Invited' })] }), showActions: true, menuItems: [
                    {
                        label: t('actions.sendEmail') || 'Send Email',
                        onClick: (row) => {
                            setMvpToSendEmail(row);
                            setSendEmailConfirmOpen(true);
                        },
                        icon: _jsx(DirectInbox, { variant: 'Outline', size: 20 }),
                        disabled: (row) => row.status === MVPStatusEnum.Registered || row.status === MVPStatusEnum.Invited
                    },
                    {
                        label: t('actions.sendReinvitationEmail') || 'Send Re-invite Email',
                        onClick: (row) => {
                            handleSendReinvitationEmail(row);
                        },
                        icon: _jsx(Sms, { variant: 'Outline', size: 20 }),
                        disabled: (row) => row.status !== MVPStatusEnum.Invited
                    },
                    {
                        label: t('actions.editRSVP') || 'Edit RSVP',
                        onClick: (row) => {
                            handleEdit(row);
                        },
                        icon: (_jsx(Iconly, { name: 'Edit', set: 'curved', stroke: 'light', style: { width: 20, height: 20 } })),
                        disabled: (row) => row.status === MVPStatusEnum.Registered // Remove from menu once registered - RSVP is locked in by then
                    },
                    {
                        label: (_jsx(Typography, { variant: 'body2', sx: { color: theme.palette.error.light }, children: t('actions.remove') || 'Remove' })),
                        onClick: (row) => {
                            handleDelete(row);
                        },
                        icon: (_jsx(Trash, { variant: 'Outline', color: theme.palette.error.light, style: { width: 20, height: 20 } })),
                        disabled: (row) => !canUpdateEvents,
                        danger: true
                    }
                ], emptyMessage: t('events.eventDetails.rsvpTracking.noRSVPsFound') || 'No RSVPs found', toolbarPosition: 'right', pagination: true, page: page, rowsPerPage: rowsPerPage, onPageChange: (newPage) => setPage(newPage), onRowsPerPageChange: (newRowsPerPage) => {
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }, totalCount: mvpsState.mvps?.totalCount, endIcon: _jsx(KeyboardArrowDown, { sx: {
                        transform: Boolean(menuAnchorEl) ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                    } }) }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleCloseMenu, actions: [
                    {
                        text: t('events.eventDetails.rsvpTracking.internal') || 'Internal',
                        group: true,
                        to: () => {
                            setOpenInternalDialog(true);
                            handleCloseMenu();
                        }
                    },
                    ...(isExternalAllowed
                        ? [
                            {
                                text: t('events.eventDetails.rsvpTracking.external') || 'External',
                                group: true,
                                to: () => {
                                    setOpenExternalDialog(true);
                                    handleCloseMenu();
                                }
                            },
                            {
                                text: t('events.eventDetails.rsvpTracking.addExternalRelation') ||
                                    'Add External Relation',
                                group: true,
                                to: () => {
                                    setOpenAddExternalRelationDialog(true);
                                    handleCloseMenu();
                                }
                            }
                        ]
                        : []),
                    {
                        text: t('events.eventDetails.rsvpTracking.importExcel') || 'Import Excel',
                        group: true,
                        to: () => {
                            setOpenImportExcelDialog(true);
                            handleCloseMenu();
                        }
                    }
                ] }), _jsx(SelectRSVPsDialog, { open: openInternalDialog, onClose: () => {
                    setOpenInternalDialog(false);
                    setMvpToEdit(null);
                }, isSmallScreen: isSmallScreen, 
                // type={editDialogType}
                type: 'internal', mvpToEdit: mvpToEdit, eventId: eventId || '', onSuccess: () => {
                    // Refresh MVP list
                    if (eventId) {
                        const payload = {
                            eventId,
                            pageNumber: page + 1,
                            pageSize: rowsPerPage
                        };
                        dispatch(getAllMVPsAsync(payload));
                    }
                } }), _jsx(SelectRSVPsDialog, { open: openExternalDialog, onClose: () => {
                    setOpenExternalDialog(false);
                    setMvpToEdit(null);
                }, isSmallScreen: isSmallScreen, type: 'external', mvpToEdit: mvpToEdit, eventId: eventId || '', onSuccess: () => {
                    // Refresh MVP list
                    if (eventId) {
                        const payload = {
                            eventId,
                            pageNumber: page + 1,
                            pageSize: rowsPerPage
                        };
                        dispatch(getAllMVPsAsync(payload));
                    }
                } }), _jsx(ImportExcelDialog, { open: openImportExcelDialog, onClose: () => setOpenImportExcelDialog(false), isSmallScreen: isSmallScreen, eventId: eventId || '', onSuccess: () => {
                    // Refresh MVP list
                    if (eventId) {
                        const payload = {
                            eventId,
                            pageNumber: page + 1,
                            pageSize: rowsPerPage
                        };
                        dispatch(getAllMVPsAsync(payload));
                    }
                } }), _jsx(AddExternalRelationDialog, { open: openAddExternalRelationDialog, onClose: () => setOpenAddExternalRelationDialog(false), isSmallScreen: isSmallScreen, eventId: eventId || '', onSuccess: () => {
                    // Refresh MVP list
                    if (eventId) {
                        const payload = {
                            eventId,
                            pageNumber: page + 1,
                            pageSize: rowsPerPage
                        };
                        dispatch(getAllMVPsAsync(payload));
                    }
                } }), _jsx(ConfirmationDialog, { open: sendEmailConfirmOpen, onClose: () => {
                    setSendEmailConfirmOpen(false);
                    setMvpToSendEmail(null);
                }, title: t('actions.sendEmail') || 'Send Email', actions: _jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', color: 'inherit', fullWidth: true, variant: 'outlined', onClick: () => {
                                setSendEmailConfirmOpen(false);
                                setMvpToSendEmail(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'primary', fullWidth: true, disabled: !canUpdateEvents, onClick: () => {
                                if (mvpToSendEmail)
                                    handleSendEmail(mvpToSendEmail);
                                setSendEmailConfirmOpen(false);
                                setMvpToSendEmail(null);
                            }, children: t('actions.sendEmail') || 'Send Email' })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('events.eventDetails.rsvpTracking.confirmSendEmailText', {
                            name: mvpToSendEmail ? `${mvpToSendEmail.firstName} ${mvpToSendEmail.lastName}` : ''
                        }) }) }) }), _jsx(ConfirmationDialog, { open: bulkSendEmailConfirmOpen, onClose: () => setBulkSendEmailConfirmOpen(false), title: t('actions.sendEmail') || 'Send Email', actions: _jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', color: 'inherit', fullWidth: true, variant: 'outlined', onClick: () => setBulkSendEmailConfirmOpen(false), children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'primary', fullWidth: true, disabled: !canUpdateEvents, onClick: () => {
                                setBulkSendEmailConfirmOpen(false);
                                handleSendBulkEmails();
                            }, children: t('actions.sendEmail') || 'Send Email' })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('events.eventDetails.rsvpTracking.confirmBulkSendEmailText', {
                            count: selectedRows.length
                        }) }) }) }), _jsx(ConfirmationDialog, { open: deleteConfirmationOpen, onClose: () => {
                    setDeleteConfirmationOpen(false);
                    setMvpToDelete(null);
                }, title: t('common.deleteConfirmation'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', color: 'inherit', fullWidth: true, variant: 'outlined', onClick: () => {
                                setDeleteConfirmationOpen(false);
                                setMvpToDelete(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'error', fullWidth: true, disabled: !canUpdateEvents, onClick: handleConfirmDelete, children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('events.eventDetails.rsvpTracking.confirmDeleteText') ||
                            'Are you sure you want to delete this RSVP? This action cannot be undone.' }) }) }), (() => {
                const handleTempChange = (field, value) => {
                    setTempFilters((prev) => ({ ...prev, [field]: value }));
                };
                const renderDropdown = (field, label, options, withFlags) => (_jsx(Autocomplete, { options: options, value: options.find((o) => o.value === tempFilters[field]) ?? null, onChange: (_, newValue) => handleTempChange(field, newValue?.value ?? null), getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, renderOption: withFlags
                        ? (props, option) => (_jsxs(Box, { component: 'li', ...props, sx: { display: 'flex', gap: 1 }, children: [option.flagUrl && (_jsx(Box, { component: 'img', src: option.flagUrl, alt: option.label, sx: { width: 20, height: 15, objectFit: 'cover', borderRadius: 0.5 } })), option.label] }))
                        : undefined, renderInput: (params) => (_jsx(HFilledTextField, { ...params, bg: 'grey8', label: label, fullWidth: true })), fullWidth: true }));
                const filterContent = (_jsxs(Box, { padding: isSmallScreen ? 2 : 3, display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(HFilledTextField, { bg: 'grey8', type: 'string', label: t('labels.firstName') || 'First Name', fullWidth: true, value: tempFilters.firstName ?? '', onChange: (e) => handleTempChange('firstName', e.target.value) }), _jsx(HFilledTextField, { bg: 'grey8', type: 'string', label: t('labels.lastName') || 'Last Name', fullWidth: true, value: tempFilters.lastName ?? '', onChange: (e) => handleTempChange('lastName', e.target.value) }), renderDropdown('company', t('labels.company') || 'Company', companyFilterOptions), renderDropdown('status', t('events.eventDetails.invitationsTab.status') || 'Status', statusFilterOptions), renderDropdown('isExternal', t('events.eventDetails.rsvpTracking.isExternal') || 'Is External', isExternalFilterOptions), renderDropdown('language', t('events.eventDetails.rsvpTracking.invitationLanguage') || 'Invitation Language', languageFilterOptions, true)] }));
                const filterActions = (_jsxs(Box, { sx: { display: 'flex', gap: 1.5, width: '100%' }, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', size: 'large', onClick: () => {
                                setTempFilters(filters);
                                setFilterDialogOpen(false);
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', onClick: () => {
                                const cleanedFilters = Object.fromEntries(Object.entries(tempFilters).filter(([_, value]) => value !== null && value !== undefined && value !== ''));
                                setFilters(cleanedFilters);
                                setFilterDialogOpen(false);
                                setPage(0);
                            }, children: t('actions.applyFilters') || 'Apply Filters' })] }));
                return isSmallScreen ? (_jsx(BDrawer, { anchor: 'bottom', title: t('actions.filters') || 'Filters', open: filterDialogOpen, onClose: () => {
                        setTempFilters(filters);
                        setFilterDialogOpen(false);
                    }, showCloseButton: true, dialogActions: filterActions, children: filterContent })) : (_jsx(CustomDialog, { backgroundVariant: 'white', open: filterDialogOpen, onClose: () => {
                        setTempFilters(filters);
                        setFilterDialogOpen(false);
                    }, title: t('actions.filters') || 'Filters', maxWidth: 'sm', actions: filterActions, children: filterContent }));
            })()] }));
};
export default RSVPTrackingTab;
